import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SYNC = { created: "Synced (new person)", matched: "Synced (existing person)", review: "Needs duplicate review", pending: "Waiting", error: "Error" };

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[var(--line)] text-sm" data-testid={testid}>
    <span className="text-muted-ink">{label}</span><span className="text-navy text-right">{children}</span>
  </div>
);

const Picker = ({ value, onChange, items, testid, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-11" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const OnboardingCard = ({ v, setV, canEdit, pro }) => {
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/onboarding/options").then((r) => setOpts(r.data)); }, []);
  if (!opts) return null;

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/volunteers/${v.volunteer_id}/onboarding`, patch);
      setV((p) => ({ ...p, onboarding: data.onboarding, background: data.background, ...(data.sync || {}) }));
      data.sync?.pco_status === "error" ? toast.error(`Saved, but Planning Center: ${data.sync.pco_error}`) : toast.success(data.sync ? "Saved and synced to Planning Center" : "Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/pco/sync/${v.volunteer_id}`);
      setV((p) => ({ ...p, ...data }));
      ["error", "review", "pending"].includes(data?.pco_status) ? toast.error(data.pco_error || SYNC[data.pco_status]) : toast.success("Synced to Planning Center");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sync");
    } finally {
      setBusy(false);
    }
  };

  const ob = v.onboarding?.status || "Assessment Completed";
  return (
    <section className="card-soft p-8" data-testid="onboarding-card">
      <p className="font-display text-navy text-xl font-semibold">Onboarding</p>
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-ink">Onboarding status</p>
          <Picker value={ob} disabled={!canEdit || busy} onChange={(s) => save({ status: s })} items={opts.statuses.map((s) => [s, s])} testid="onboarding-status-select" />
          {v.onboarding?.by && <p className="text-xs text-muted-ink">Set by {v.onboarding.by} · {new Date(v.onboarding.at).toLocaleString()}</p>}
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-ink">Background check</p>
          <Picker value={v.background?.status || "unknown"} disabled={!canEdit || busy} onChange={(s) => save({ background: s })} items={Object.entries(opts.background)} testid="background-check-select" />
          <p className="text-xs text-muted-ink">{v.background?.by ? `Updated by ${v.background.by} · ${new Date(v.background.at).toLocaleString()}` : "Approved to Serve, Assigned and Active need this Verified or Not required."}</p>
        </div>
      </div>
      {pro && <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-ink mb-1">Planning Center</p>
        <Row label="Consent to sync" testid="pco-consent-row">{v.pco_consent ? "Yes" : "No"}</Row>
        <Row label="Person ID" testid="pco-person-id-row">{v.pco_person_id ? `#${v.pco_person_id}` : "—"}</Row>
        <Row label="Sync status" testid="pco-sync-status-row">{SYNC[v.pco_status] || "Not synced yet"}</Row>
        <Row label="Last successful sync" testid="pco-last-sync-row">{v.pco_synced_at ? new Date(v.pco_synced_at).toLocaleString() : "—"}</Row>
        {v.pco_error && <p className="text-sm text-red-700 mt-2" data-testid="pco-error-row">{v.pco_error}</p>}
        {canEdit && v.pco_consent && <button onClick={sync} disabled={busy} className="chip-btn mt-4 flex items-center gap-1.5" data-testid="pco-sync-now-btn"><RefreshCw size={13} /> Sync to Planning Center</button>}
        {!v.pco_consent && <p className="text-sm text-muted-ink mt-2" data-testid="pco-no-consent-note">This volunteer didn't agree to be added to Planning Center, so nothing is sent for them.</p>}
      </div>}
    </section>
  );
};
